/**
 * The crosstab DANI's report planner is handed, from the engine (M4S-5777 X125).
 *
 * The planner's crosstab step used to send `buildCrosstabContext`: a question inventory from the
 * classic data service, with no figures, so every number in the report was the agent's own. For a
 * saved AI crosstab this hands DANI the engine's figures instead: the record is computed on the
 * server with everything it holds (`/api/crosstab/project/[id]/computed` with its key, the one
 * engine Slide Creator also uses), summarised by `daniTableContext`, with the statistics the
 * crosstab uses in the legend's words. A classic crosstab (no AI layout) answers null, and the
 * caller keeps the inventory, labelled as one.
 *
 * Browser-side; the computing happens on the server.
 */
import { crosstabLibraryService } from '@/lib/crosstab/library/list-service';
import { loadCrosstabTable } from '@/@studio-ai/components/slide-creation/load-crosstab';
import { readAiCrosstabState } from '@/lib/crosstab/ai-layout';
import { daniStatsContext } from '@/lib/crosstab/dani/dani-context';
import { daniTableContext } from '@/lib/crosstab/dani-table-context';
import { stampOf, UUID_RE } from '@/lib/crosstab/saved-survey-scope';
import { parseStatSettings, statFields } from '@/lib/crosstab/stat-settings';

/** The planner's message limit is 10,000 characters; the figures get this much of it. */
export const PLANNER_FIGURES_MAX_CHARS = 5500;

export const ENGINE_FIGURES_NOTE =
    'These are the AI Crosstabs engine’s figures for the saved crosstab, computed with its own variables, filters, weighting and statistics. Quote them exactly; never restate or recompute them.';

/** The engine's figures for a saved AI crosstab, or null for a classic crosstab. Throws on a failed compute. */
export async function engineCrosstabContext(
    crosstabId: string,
    maxChars = PLANNER_FIGURES_MAX_CHARS,
): Promise<Record<string, unknown> | null> {
    if (!UUID_RE.test(crosstabId)) return null;
    const record = (await crosstabLibraryService.getCrosstab(crosstabId)) as {
        aiLayout?: unknown;
        projectId?: unknown;
        name?: unknown;
    };
    const state = readAiCrosstabState({ aiLayout: record?.aiLayout });
    if (!state) return null;
    const rows = Array.isArray(state.activeRows) ? state.activeRows.map(String) : [];
    const banner = Array.isArray(state.activeBanner) ? state.activeBanner.map(String) : [];
    const projectId = String(record.projectId ?? '').trim();
    const table = await loadCrosstabTable(projectId || 'saved', {
        activeRows: rows,
        activeBanner: banner,
        layoutKey: crosstabId,
    });
    const stats = parseStatSettings(state.statSettings);
    const fields = stats ? statFields(stats) : null;
    return {
        source: 'AI Crosstabs engine',
        note: ENGINE_FIGURES_NOTE,
        name: typeof record.name === 'string' ? record.name : null,
        projectId,
        surveyIds: stampOf(state)?.surveyIds ?? [],
        totalRespondents: table.totalN,
        // A crosstab saved before its statistics were kept with it (X14) states none: the engine
        // used the study type's starting statistics, which this side cannot name for certain.
        ...(fields ? { statistics: daniStatsContext(fields, { computed: table }) } : {}),
        table: daniTableContext(table, { decimals: fields?.decimalPlaces ?? 0, maxChars }),
    };
}

/** A classic crosstab's question inventory, labelled so DANI states no figure as the crosstab's. */
export function inventoryOnly(context: Record<string, unknown>): Record<string, unknown> {
    const label =
        'Question inventory only (a classic crosstab): there are no figures here, so do not state any as the crosstab’s.';
    return { ...context, note: [label, context.note].filter(Boolean).join(' ') };
}
